import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { crossSectionArmedAtom } from './atoms';
import { CrossSectionPanel } from './cross-section-panel';
import styles from './cross-section-map-drawer.module.scss';

/** Where the drawer opens, as a share of the stage it sits in. */
const DEFAULT_HEIGHT_FRACTION = 0.42;

/** How far it can be dragged, so neither the map nor the profile is squeezed out. */
const MIN_HEIGHT_PX = 180;
const MAX_HEIGHT_FRACTION = 0.8;

/** How far the keyboard moves the handle per press. */
const KEYBOARD_STEP_PX = 24;

/**
 * The section drawer for a page that is all map.
 *
 * On a page with a results region the drawer takes that region and the page owns the
 * split. A full-bleed map has no such region, so here the drawer sits over the foot
 * of the map — which is what the mock does on the main map — with a handle to trade
 * height against it.
 *
 * The map keeps its own height either way: covering the bottom of it is the point,
 * since the two views are the same section in two projections and hiding one to read
 * the other defeats the exercise.
 *
 * @return {JSX.Element | null} The drawer over the map, or nothing.
 */
export const CrossSectionMapDrawer = (): JSX.Element | null => {
  const armed = useAtomValue(crossSectionArmedAtom);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | null>(null);
  const dragFrom = useRef<{ y: number; height: number } | null>(null);

  const clamp = useCallback((next: number) => {
    const stage = hostRef.current?.parentElement?.clientHeight ?? 0;
    const max = stage > 0 ? stage * MAX_HEIGHT_FRACTION : next;
    return Math.min(Math.max(next, MIN_HEIGHT_PX), max);
  }, []);

  // Opened proportional to the viewport rather than at a fixed height: the same
  // drawer on a laptop and on a wall display should give up the same share of map.
  useEffect(() => {
    if (!armed) {
      setHeight(null);
      return;
    }
    const stage = hostRef.current?.parentElement?.clientHeight ?? 0;
    if (stage > 0) {
      setHeight(clamp(stage * DEFAULT_HEIGHT_FRACTION));
    }
  }, [armed, clamp]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    const current = hostRef.current?.clientHeight ?? 0;
    dragFrom.current = { y: event.clientY, height: current };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const from = dragFrom.current;
    if (!from) {
      return;
    }
    // Dragging up makes the drawer taller, which is the direction the handle moves.
    setHeight(clamp(from.height + (from.y - event.clientY)));
  };

  const onPointerUp = () => {
    dragFrom.current = null;
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
      return;
    }
    event.preventDefault();
    const current = hostRef.current?.clientHeight ?? 0;
    const step = event.key === 'ArrowUp' ? KEYBOARD_STEP_PX : -KEYBOARD_STEP_PX;
    setHeight(clamp(current + step));
  };

  if (!armed) {
    return null;
  }

  return (
    <div
      ref={hostRef}
      className={styles.host}
      style={height === null ? undefined : { height: `${height}px` }}
    >
      <div
        className={styles.handle}
        role="separator"
        tabIndex={0}
        aria-orientation="horizontal"
        aria-label="Resize cross section drawer"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onKeyDown={onKeyDown}
        // Back to the height it opened at, which is the cheapest way out of a
        // drag that went too far.
        onDoubleClick={() => setHeight(null)}
      >
        <span className={styles.grip} aria-hidden="true" />
      </div>
      <CrossSectionPanel />
    </div>
  );
};
